import type { Event, StackFrame } from '@sentry/react'

const CEF_SHARP_OBJECT_NOT_FOUND_PATTERN = /(?:^|:\s)Object Not Found Matching Id:\d+, MethodName:[^,]+, ParamCount:\d+$/
const FIRST_PARTY_HOST_SUFFIXES = ['.supplyco.ai', '.orb.local']
const LOCALHOSTS = new Set(['localhost', '127.0.0.1', '::1'])
const FAILED_FETCH_HOST_PATTERN = /^Failed to fetch \(([^)]+)\)$/

function getFramePaths(frame: StackFrame): string[] {
  const { filename, module } = frame
  const { abs_path: absPath } = frame as StackFrame & { abs_path?: string }

  return [filename, module, absPath].filter((path): path is string => !!path)
}

function isSentrySdkFrame(frame: StackFrame): boolean {
  const module = frame.module ?? ''

  return (
    getFramePaths(frame).some(path =>
      path.includes('node_modules/@sentry/')
      || path.includes('node_modules/.pnpm/@sentry+'),
    )
    || module.startsWith('@sentry/')
  )
}

function isVercelLiveFeedbackFrame(frame: StackFrame): boolean {
  return getFramePaths(frame).some(path =>
    path.includes('_next-live/feedback/')
    || path.includes('feedback/instrument'),
  )
}

function isFirstPartyHost(hostname: string): boolean {
  const normalizedHostname = hostname.toLowerCase()

  return (
    LOCALHOSTS.has(normalizedHostname)
    || FIRST_PARTY_HOST_SUFFIXES.some(suffix =>
      normalizedHostname === suffix.slice(1) || normalizedHostname.endsWith(suffix),
    )
  )
}

function getThirdPartyHost(url: string): string | undefined {
  try {
    const hostname = new URL(url, 'http://localhost').hostname.toLowerCase()

    return isFirstPartyHost(hostname) ? undefined : hostname
  }
  catch {
    return undefined
  }
}

function getBreadcrumbUrl(data: { url?: unknown } | undefined): string | undefined {
  const url = data?.url

  return typeof url === 'string' ? url : undefined
}

function hasFirstPartyAppFrame(frames: StackFrame[]): boolean {
  return frames.some(frame =>
    getFramePaths(frame).some(path =>
      path.includes('/src/')
      || path.includes('/frontend/src/')
      || path.includes('/assets/'),
    ),
  )
}

function getFailedFetchHost(value: string | undefined): string | undefined {
  return value?.match(FAILED_FETCH_HOST_PATTERN)?.[1]?.toLowerCase()
}

export function isVercelLiveFeedbackEvent(event: Event): boolean {
  return event.exception?.values?.some((exception) => {
    const frames = exception.stacktrace?.frames ?? []
    const nonSentryFrames = frames.filter(frame => !isSentrySdkFrame(frame))

    return nonSentryFrames.length > 0 && nonSentryFrames.every(frame =>
      isVercelLiveFeedbackFrame(frame),
    )
  }) ?? false
}

export function isCefSharpBrowserBotEvent(event: Event): boolean {
  return event.exception?.values?.some(exception =>
    exception.type === 'UnhandledRejection'
    && exception.mechanism?.type === 'auto.browser.global_handlers.onunhandledrejection'
    && CEF_SHARP_OBJECT_NOT_FOUND_PATTERN.test(exception.value ?? ''),
  ) ?? false
}

export function isThirdPartyInjectedFetchFailureEvent(event: Event): boolean {
  const thirdPartyFetchHosts = event.breadcrumbs?.flatMap((breadcrumb) => {
    if (breadcrumb.category !== 'fetch' || breadcrumb.level !== 'error') {
      return []
    }

    const url = getBreadcrumbUrl(breadcrumb.data)
    const hostname = url ? getThirdPartyHost(url) : undefined

    return hostname ? [hostname] : []
  }) ?? []

  if (thirdPartyFetchHosts.length === 0) {
    return false
  }

  return event.exception?.values?.some((exception) => {
    const frames = exception.stacktrace?.frames ?? []
    const failedFetchHost = getFailedFetchHost(exception.value)

    return (
      exception.type === 'TypeError'
      && !!failedFetchHost
      && thirdPartyFetchHosts.includes(failedFetchHost)
      && exception.mechanism?.type === 'auto.browser.global_handlers.onunhandledrejection'
      && frames.length > 0
      && !hasFirstPartyAppFrame(frames)
    )
  }) ?? false
}
